/**
 * Clue2App design system — shared tokens.
 *
 * Pulled out of the showcase/wizard so other pages can adopt the
 * same dark + glass + brand-orange aesthetic without re-deriving
 * everything. Import what you need, compose into MUI `sx` props.
 *
 * Tokens are intentionally light. The console's MUI theme still owns
 * typography, spacing rhythm, default colors. This file adds:
 *   - brand orange palette + gradient sets
 *   - glass-morphism surface recipes
 *   - dark-page background recipes (matches /showcase)
 *   - framer-motion variants for stagger-fade-up entrances
 *   - one-line helpers for hover lift + brand glow
 */

export const brand = {
  orange50: "#FFF7ED",
  orange100: "#FFEDD5",
  orange200: "#FED7AA",
  orange300: "#FDBA74",
  orange400: "#FB923C",
  orange500: "#F97316",
  orange600: "#EA580C",
  orange700: "#C2410C",
  orange800: "#9A3412",
};

// Dark surface palette — matches the showcase page background.
export const darkSurface = {
  base: "#0a0e27",
  raised: "#0f172a",
  card: "#131a3a",
  border: "rgba(255,255,255,0.08)",
  borderStrong: "rgba(255,255,255,0.12)",
  text: "rgba(255,255,255,0.92)",
  textMuted: "rgba(255,255,255,0.65)",
  textFaint: "rgba(255,255,255,0.45)",
};

export const gradients = {
  brand: `linear-gradient(135deg, ${brand.orange600} 0%, ${brand.orange500} 100%)`,
  brandSoft: `linear-gradient(135deg, ${brand.orange500} 0%, ${brand.orange400} 100%)`,
  brandHover: `linear-gradient(135deg, ${brand.orange700} 0%, ${brand.orange600} 100%)`,
  pageDark:
    "radial-gradient(ellipse at 20% 0%, rgba(234,88,12,0.10), transparent 50%), " +
    "radial-gradient(ellipse at 80% 100%, rgba(99,102,241,0.10), transparent 55%), " +
    `linear-gradient(180deg, ${darkSurface.base} 0%, ${darkSurface.raised} 60%, ${darkSurface.card} 100%)`,
  heroText:
    "linear-gradient(135deg, #ffffff 0%, #fed7aa 50%, #fdba74 100%)",
};

// Glass-morphism card recipe. Plug into sx={glassCard(isDarkMode)}.
export const glassCard = (isDarkMode = true) => ({
  borderRadius: 3,
  background: isDarkMode
    ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
    : "rgba(255,255,255,0.7)",
  border: `1px solid ${isDarkMode ? darkSurface.border : "rgba(15,23,42,0.06)"}`,
  backdropFilter: "blur(20px)",
  boxShadow: isDarkMode
    ? "0 4px 14px rgba(0,0,0,0.35)"
    : "0 1px 3px rgba(15,23,42,0.06)",
  transition: "border-color 200ms ease, box-shadow 200ms ease, transform 200ms ease",
});

// Hover state for a glass card — add as `&:hover` inside sx.
export const glassCardHover = (isDarkMode = true) => ({
  transform: "translateY(-2px)",
  borderColor: isDarkMode ? "rgba(253,186,116,0.4)" : "rgba(234,88,12,0.25)",
  boxShadow: isDarkMode
    ? "0 16px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(234,88,12,0.3), 0 0 32px rgba(234,88,12,0.15)"
    : "0 8px 24px rgba(15,23,42,0.10), 0 0 0 1px rgba(234,88,12,0.18)",
});

// Brand-orange button recipe. `variant="contained"` + sx={brandButton()}.
export const brandButton = () => ({
  background: gradients.brand,
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "10px",
  px: 2,
  boxShadow: "0 2px 12px rgba(234,88,12,0.35)",
  "&:hover": {
    background: gradients.brandHover,
    boxShadow: "0 4px 20px rgba(234,88,12,0.45)",
  },
});

// Hero gradient text — theme-aware. The white→amber gradient
// disappears on a light bg, so light mode uses an orange-saturated
// gradient that reads well on warm-white.
export const heroText = (isDarkMode = true) => ({
  fontWeight: 800,
  letterSpacing: "-0.02em",
  background: isDarkMode
    ? gradients.heroText
    : `linear-gradient(135deg, ${brand.orange700} 0%, ${brand.orange500} 60%, ${brand.orange400} 100%)`,
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  color: "transparent",
  textShadow: isDarkMode
    ? "0 4px 30px rgba(234,88,12,0.15)"
    : "0 1px 1px rgba(255,255,255,0.5)",
});

// Page background for any top-level page wanting the dark/showcase
// aesthetic. Apply to the outermost Box. Light mode = subtle warm gradient.
export const pageBackground = (isDarkMode = true) =>
  isDarkMode
    ? { minHeight: "100%", background: gradients.pageDark, color: darkSurface.text }
    : {
        minHeight: "100%",
        background:
          "radial-gradient(ellipse at 20% 0%, rgba(234,88,12,0.05), transparent 50%), " +
          "linear-gradient(180deg, #ffffff 0%, #f8fafc 60%, #f1f5f9 100%)",
        color: "#0f172a",
      };

// Framer-motion variants — stagger fade-up for grids / lists.
export const motionVariants = {
  // Apply to a parent container. Children with childFadeUp will stagger.
  staggerParent: {
    hidden: {},
    show: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
  },
  childFadeUp: {
    hidden: { opacity: 0, y: 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.4, 0, 0.2, 1] } },
  },
  // Drop-in for a single page hero entrance.
  heroEnter: {
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  },
};
